/**
 * Store for owner's restaurant list and total pending reservations count.
 * Used by AppLayout (badge) and ReservationsView. Call refresh() on mount and after reservation actions.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { restaurantService } from '@/services'
import Restaurant from '@/models/Restaurant'

export const useOwnerRestaurantsStore = defineStore('ownerRestaurants', () => {
  const restaurants = ref([])
  const loading = ref(false)
  /** True after the first refresh() has completed for the current session; reset on logout. Used so views don't show "no businesses" before load completes. */
  const hasFetched = ref(false)
  /** Ignore stale refresh() results when a newer refresh started (e.g. logout during fetch). */
  let refreshGeneration = 0
  /** Single in-flight list request: concurrent refresh() calls share one HTTP GET (fixes duplicate /restaurants?per_page=50 on load). */
  let inFlight = null

  const totalPendingReservations = computed(() => {
    return restaurants.value.reduce((sum, r) => sum + (r.pending_reservations_count ?? 0), 0)
  })

  /**
   * Fetch owner's restaurants (list). Updates restaurants and thus totalPendingReservations.
   * Call when user is owner (not superadmin). Safe to call repeatedly.
   * Concurrent callers await the same request until it completes.
   */
  function refresh() {
    if (inFlight) return inFlight

    const gen = ++refreshGeneration
    loading.value = true

    const run = async () => {
      try {
        const data = await restaurantService.list({ per_page: 50 })
        if (gen !== refreshGeneration) return
        const list = Array.isArray(data?.data) ? data.data : []
        restaurants.value = list.map((d) => Restaurant.fromApi(d))
      } catch {
        if (gen !== refreshGeneration) return
        restaurants.value = []
      } finally {
        if (gen === refreshGeneration) {
          loading.value = false
          hasFetched.value = true
        }
        if (inFlight === p) inFlight = null
      }
    }

    const p = run()
    inFlight = p
    return p
  }

  /** Clear all data (call on logout so the next user doesn't see the previous user's businesses). */
  function reset() {
    refreshGeneration += 1
    inFlight = null
    restaurants.value = []
    loading.value = false
    hasFetched.value = false
  }

  return {
    restaurants,
    loading,
    hasFetched,
    totalPendingReservations,
    refresh,
    reset,
  }
})
